import React, { useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components/macro";

import RadioQuestion from "../../../components/RadioQuestion";
import ScrollToTop from "../../../components/utils/ScrollToTop";
import { useSendEstateItem } from "../../../hooks/useSentEstateItems";
import { Button } from "../../../probate/components/ui/button";
import { TrayActions } from "../../../probate/components/tray";
import { estateItemSubmissions } from "../../../shared/probateEstate/estateItemPayload";
import { ADDABLE_TYPES, addFormCopy, ESTATE_ITEM_TYPES } from "../../../shared/probateEstate/estateItemTypes";
import { captureSubmissionError, isNetworkError } from "../../../utils/sentryUtils";
import { useSelectedCase } from "../utils/hooks";
import EstateItemFields, { FIELD_ORDER, validateEstateItem } from "./EstateItemFields";
import { Questions, TaskError } from "./ProbateDoable";
import ProbatePage from "./ProbatePage";

// The questions at the sheet body's 24px inset, with the button after them
const AddForm = styled(Questions).attrs({ as: "form", noValidate: true })`
  padding: 0 var(--juno-space-6) var(--juno-space-4);
`;

const Sent = styled.p`
  margin: 0;
`;

const allowedTypes = (param) => {
  const types = [...new Set((param ?? "").split(","))].filter((type) => ADDABLE_TYPES.includes(type));
  return types.length > 0 ? types : ADDABLE_TYPES;
};

// Only a dashboard page, so the link can't take the client off the app
const returnPath = (param, caseRef) => (param?.startsWith("/dashboard/") ? param : `/dashboard/${caseRef}/estate`);

// The kind of questions to ask: the chosen type's, or the one kind every allowed type shares
const kindOf = (type, types) => {
  if (type) return ESTATE_ITEM_TYPES[type].fields;
  const kinds = new Set(types.map((t) => ESTATE_ITEM_TYPES[t].fields));
  return kinds.size === 1 ? [...kinds][0] : null;
};

const ERROR = "We couldn’t add this. Try again, or send us a message using web chat.";

export default function ProbateAddEstateItem() {
  const kase = useSelectedCase({ caseScreen: "estate" });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // The back link of the request that opened Add, handed back on return so the request keeps it
  const requestBack = useLocation().state?.back;
  const sendEstateItem = useSendEstateItem(kase);

  const types = allowedTypes(searchParams.get("types"));
  const [type, setType] = useState(types.length === 1 ? types[0] : null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  // State lags a second submit in the same tick, and there's no idempotency key yet
  const sendingRef = useRef(false);
  // The submissions that have gone, by content, so a retry after a failed vehicle finance doesn't
  // send the vehicle again, while anything the client changes still goes
  const sentRef = useRef(new Set());
  // The name of the item whose first submission has gone, which locks its questions
  const [sentName, setSentName] = useState(null);
  const fieldRefs = useRef({});

  if (!kase) {
    return null;
  }

  const back = returnPath(searchParams.get("back"), kase.case_reference);
  const { addLabel, nameHint } = addFormCopy(types);
  const kind = kindOf(type, types);

  const onChange = (name) => (value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const send = async () => {
    const found = validateEstateItem(type, kind, values);
    setErrors(found);
    const firstError = FIELD_ORDER.find((name) => found[name]);
    if (firstError) {
      fieldRefs.current[firstError]?.querySelector("input")?.focus();
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setSendFailed(false);
    const submissions = estateItemSubmissions(type, values);
    for (const submission of submissions) {
      const key = JSON.stringify(submission);
      if (sentRef.current.has(key)) continue;
      try {
        await sendEstateItem(submission);
      } catch (error) {
        if (!isNetworkError(error)) {
          captureSubmissionError("EstateItem", error.status ?? 0, String(error));
        }
        sendingRef.current = false;
        setSending(false);
        setSendFailed(true);
        return;
      }
      sentRef.current.add(key);
      setSentName((name) => name ?? submission.payload.institution?.name ?? submission.payload.description);
    }
    const { institution, description } = submissions[0].payload;
    navigate(back, { state: { addedEstateItem: institution?.name ?? description, back: requestBack } });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!sendingRef.current) send();
  };

  return (
    <ProbatePage
      caseRef={kase.case_reference}
      title={addLabel}
      back={{ to: back, label: "Back", state: requestBack && { back: requestBack } }}
    >
      <ScrollToTop />
      <AddForm onSubmit={onSubmit}>
        {types.length > 1 && (
          <div ref={(el) => (fieldRefs.current.type = el)}>
            <RadioQuestion
              question="What is it?"
              name="item_type"
              options={types.map((t) => ({ value: t, label: ESTATE_ITEM_TYPES[t].label }))}
              selected={type}
              disabled={sentName != null}
              optionSelected={(value) => {
                setType(value);
                setErrors({});
              }}
              validationError={errors.type}
            />
          </div>
        )}
        {sentName != null && <Sent>We’ve already got “{sentName}”, so you can’t change it here.</Sent>}
        <EstateItemFields
          locked={sentName != null}
          kind={kind}
          type={type && ESTATE_ITEM_TYPES[type]}
          nameHint={nameHint ?? (type && ESTATE_ITEM_TYPES[type].nameHint)}
          values={values}
          errors={errors}
          onChange={onChange}
          fieldRefs={fieldRefs}
        />
        {sendFailed && <TaskError role="alert">{ERROR}</TaskError>}
        <TrayActions>
          <Button type="submit" variant="secondary" disabled={sending}>
            Add
          </Button>
        </TrayActions>
      </AddForm>
    </ProbatePage>
  );
}
